import { COMPANY, INDIVIDUAL } from '@constants/customerTypes'
import {
  CREDIT_CARD,
  INVOICE,
  PAYPAL_CHECKOUT,
  PRZELEWY24
} from '@constants/paymentMethods'
import { isEuropeanUnion } from '@pages/Checkout/helpers/billing'
import { CountriesType } from '@services/payments/useGetCountries'
import { t } from 'i18next'
import { z } from 'zod'
import { toFormikValidationSchema } from 'zod-formik-adapter'

import {
  isCardNumberHasProperLength,
  isCardSupported,
  isCorrectCardExpiration,
  isCvcValid
} from './creditCard'

interface SchemaProps {
  countriesList: CountriesType[]
  isPaymentMethodForm: boolean
}

const schema = ({ countriesList, isPaymentMethodForm }: SchemaProps) =>
  z
    .object({
      customerType: z.enum([COMPANY, INDIVIDUAL]).optional(),
      country: z.string().optional(),
      companyName: z.string().optional(),
      nameAndSurname: z.string().optional(),
      vat: z.string().optional(),
      address: z.string().optional(),
      postCode: z.string().optional(),
      city: z.string().optional(),
      paymentMethod: z.enum([
        CREDIT_CARD,
        PAYPAL_CHECKOUT,
        PRZELEWY24,
        INVOICE
      ]),
      cardNumber: z.string().optional(),
      cardExpiry: z.string().optional(),
      cardCvv: z.string().optional()
    })
    .superRefine(
      (
        {
          customerType,
          country,
          companyName,
          nameAndSurname,
          vat,
          address,
          postCode,
          city,
          paymentMethod,
          cardNumber,
          cardExpiry,
          cardCvv
        },
        ctx
      ) => {
        if (!country) {
          ctx.addIssue({
            path: ['country'],
            code: z.ZodIssueCode.custom,
            message: t('form.validation.isRequired')
          })
        }

        if (customerType === COMPANY && !companyName) {
          ctx.addIssue({
            path: ['companyName'],
            code: z.ZodIssueCode.custom,
            message: t('form.validation.isRequired')
          })
        }

        if (customerType === INDIVIDUAL && !nameAndSurname) {
          ctx.addIssue({
            path: ['nameAndSurname'],
            code: z.ZodIssueCode.custom,
            message: t('form.validation.isRequired')
          })
        }

        if (
          country &&
          customerType === COMPANY &&
          isEuropeanUnion({
            countries: countriesList,
            country
          }) &&
          !vat
        ) {
          ctx.addIssue({
            path: ['vat'],
            code: z.ZodIssueCode.custom,
            message: t('form.validation.isRequired')
          })
        }

        if (!address) {
          ctx.addIssue({
            path: ['address'],
            code: z.ZodIssueCode.custom,
            message: t('form.validation.isRequired')
          })
        }

        if (!postCode) {
          ctx.addIssue({
            path: ['postCode'],
            code: z.ZodIssueCode.custom,
            message: t('form.validation.isRequired')
          })
        }

        if (country === 'PL' && postCode) {
          const polishPostCodeRegex = /^\d{2}-\d{3}$/

          if (!polishPostCodeRegex.test(postCode)) {
            ctx.addIssue({
              path: ['postCode'],
              code: z.ZodIssueCode.custom,
              message: t('word.message.credit.card.step.post.code.error')
            })
          }
        }

        if (!city) {
          ctx.addIssue({
            path: ['city'],
            code: z.ZodIssueCode.custom,
            message: t('form.validation.isRequired')
          })
        }

        if (!paymentMethod) {
          ctx.addIssue({
            path: ['paymentMethod'],
            code: z.ZodIssueCode.custom,
            message: t('form.validation.isRequired')
          })
        }

        if (paymentMethod === CREDIT_CARD && isPaymentMethodForm) {
          if (!cardNumber || isCardNumberHasProperLength(cardNumber)) {
            ctx.addIssue({
              path: ['cardNumber'],
              code: z.ZodIssueCode.custom,
              message: t('form.validation.isRequired')
            })
          } else if (cardNumber && !isCardSupported(cardNumber)) {
            ctx.addIssue({
              path: ['cardNumber'],
              code: z.ZodIssueCode.custom,
              message: `${t('word.message.credit.card.step.error.no.support')} ${t('support.email')}`
            })
          }

          if (!cardExpiry || isCorrectCardExpiration(cardExpiry)) {
            ctx.addIssue({
              path: ['cardExpiry'],
              code: z.ZodIssueCode.custom,
              message: t('form.validation.isRequired')
            })
          }

          if (!cardCvv || !isCvcValid(cardCvv, cardNumber)) {
            ctx.addIssue({
              path: ['cardCvv'],
              code: z.ZodIssueCode.custom,
              message: t('form.validation.isRequired')
            })
          }
        }
      }
    )

export const getValidationSchema = ({
  countriesList,
  isPaymentMethodForm
}: SchemaProps) =>
  toFormikValidationSchema(schema({ countriesList, isPaymentMethodForm }))
